import { Close } from '@casumo/dojo-react-icons';
import {
  FloatingFocusManager,
  FloatingOverlay,
  FloatingPortal,
  useClick,
  useDismiss,
  useFloating,
  useId,
  useInteractions,
  useRole,
  useTransitionStyles,
} from '@floating-ui/react';
import classNames from 'classnames';
import { CSSProperties, FC, ReactNode, useEffect, useState } from 'react';
import { CasumoLoaderIcon } from '../assets/icons';
import { useRegistrationStore } from '../store/store';
import { TEST_ID } from '../utils/constants';

type DialogProps = {
  isOpen: boolean;
  setIsOpen?: (isOpen: boolean) => void;
  onClose?: () => void;
  children: ReactNode;
  overlayClass?: string;
  dialogClass?: string;
  style?: CSSProperties;
  isDrawer?: boolean;
  height?: string;
  hideClose?: boolean;
};

const Dialog: FC<DialogProps> = ({
  isOpen,
  setIsOpen,
  onClose,
  children,
  overlayClass,
  dialogClass,
  style,
  isDrawer,
  height,
  hideClose,
}) => {
  const [isMobile, setIsMobile] = useState(false);
  const { rootLoading } = useRegistrationStore();

  const { refs, context } = useFloating({
    open:         isOpen,
    onOpenChange: (open) => {
      if (!open && onClose) {
        onClose();
      }
      setIsOpen?.(open);
    },
  });

  const { isMounted, styles } = useTransitionStyles(context, {
    initial: {
      opacity:   0,
      transform: isMobile && isDrawer ? `translateY(${height})` : '',
    },
    open: {
      opacity:    1,
      transform:  'translateY(0)',
      transition: 'opacity 0.30s ease-out',
    },
  });

  const responsiveSizes =
    'md:w-[75vw] md:h-[90dvh] ' +
    'lg:w-[614px] lg:h-[90dvh] ' +
    'xl:w-[664px] 2xl:w-[680px] ' +
    '[@media(max-height:600px)]:md:h-[100dvh]';

  const click = useClick(context);
  const role = useRole(context);
  const dismiss = useDismiss(context, {
    escapeKey:    true,
    outsidePress: !!isDrawer,
  });

  const { getFloatingProps } = useInteractions([
    click,
    role,
    dismiss,
  ]);

  const headingId = useId();
  const descriptionId = useId();

  const handleClose = () => {
    onClose?.();
    setIsOpen?.(false);
  }

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const root = document.getElementById('fabric-registration-v2');

  const Loader = <div className="animate-spin rounded-md">
    <CasumoLoaderIcon width={64} height={64} />{rootLoading}
  </div>;

  return (
    isOpen && isMounted && (
      <FloatingPortal root={root}>
        <FloatingOverlay
          className={classNames(
            'flex justify-center md:items-end z-modal',
            overlayClass,
          )}
          lockScroll
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <FloatingFocusManager context={context} returnFocus={false} initialFocus={-1}>
            <div className="md:relative md:max-w-fit w-full">
              <div
                className={classNames(
                  'flex flex-col justify-center items-center w-full',
                  'shadow-xl overflow-y-auto focus:outline-none',
                  dialogClass,
                  {
                    'bg-gray-100 h-full':                      !dialogClass,
                    [responsiveSizes]:                         !dialogClass,
                    'lg:rounded-t-[32px] md:rounded-t-[32px]': !dialogClass,
                  },
                  `${rootLoading ? '[&:not(:first-child)]:*:hidden' : 'first:*:hidden'}`,
                )}
                ref={refs.setFloating}
                aria-labelledby={headingId}
                aria-describedby={descriptionId}
                {...getFloatingProps()}
                style={{ ...styles, ...style }}
              >
                {Loader}
                {children}
              </div>
              {
                !isDrawer && !hideClose &&
                  <button
                    onClick={handleClose}
                    data-testid={TEST_ID.BUTTON_CLOSE}
                    className="absolute hidden md:flex items-center justify-center top-0 right-0 -mx-2xl -my-2xl
                translate-x-full translate-y-full
                focus:outline-none focus:ring-0.5 focus:ring-offset-0 focus:ring-purpleblue
                  bg-purple-90 text-white text-[24px] w-2xl h-2xl rounded-full"
                  >
                    <Close />
                  </button>
              }
            </div>
          </FloatingFocusManager>
        </FloatingOverlay>
      </FloatingPortal>
    )
  );
};

export default Dialog;
